import { useCallback, useMemo } from "react";
import {
  EntityPicker,
  toastManager,
  useIsMobile,
  type EntityPickerNoItems,
  type EntitySelection,
  type EntitySelectorConfig,
} from "@salestrak/ui";

import {
  ISSUE_POOL_PAGE_SIZE,
  useIssuePool,
} from "#app/components/entity-selectors/hooks";
import type { FormFillIssue } from "#app/lib/form-fill-dialog-context";
import { useFetchIssueById } from "#app/lib/hooks/use-fetch-issue-by-id";
import {
  offeredOnWhere,
  useFormsStore,
} from "#app/lib/forms";
import { toastMutationError } from "#app/lib/utils/toast-errors";

/** A pool row carries only ids, so the pick is resolved to the full row (`viewerCanEdit` refuses read-only Projects). */
export function FillIssuePicker({
  formId,
  formName,
  open,
  onOpenChange,
  onPick,
}: {
  formId: string | null;
  formName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (issue: FormFillIssue) => void;
}) {
  const isMobile = useIsMobile();
  const { formsById } = useFormsStore(null, { skip: !formId });
  const offer = formId ? formsById.get(formId)?.settings?.offeredOn : undefined;
  const where = useMemo(() => offeredOnWhere(offer), [offer]);
  const { pool } = useIssuePool(where ? { where } : undefined);
  const fetchIssueById = useFetchIssueById();

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      emptyMessage: "No Projects found",
      maxSelections: 1,
      pools: where === null ? [] : [pool],
      searchPlaceholder: "Search Projects by name or address…",
    }),
    [pool, where],
  );

  const noItems = useMemo<EntityPickerNoItems>(
    () =>
      where === null
        ? {
            description: "Pick where this form is offered in its settings.",
            icon: "folder-kanban",
            title: "This form isn't offered on any Project",
          }
        : {
            description: "Search by name or address to find the Project to fill this out on.",
            icon: "folder-kanban",
            title: "No Projects yet",
          },
    [where],
  );

  const handleConfirm = useCallback(
    (selections: EntitySelection[]) => {
      const picked = selections[0];
      if (!picked) return;
      fetchIssueById(picked.id)
        .then((issue) => {
          // Filling writes the Project's linked fields, so read-only Projects are refused.
          if (issue && !issue.viewerCanEdit) {
            toastManager.add({ title: "You can't edit this Project", type: "error" });
            return;
          }
          if (issue) {
            onPick(issue);
            return;
          }
          // The query succeeded with nothing: the Project is deleted or invisible, which a pool row can outlive.
          toastManager.add({
            title: "You do not have access to this Project",
            type: "error",
          });
        })
        .catch((err) => {
          // A real fetch failure, not an access problem: do not claim missing access.
          toastMutationError(err, "Failed to open Project");
        });
    },
    [fetchIssueById, onPick],
  );

  return (
    <EntityPicker
      config={config}
      description={`Pick the Project "${formName}" is filled out on.`}
      noItems={noItems}
      onConfirm={handleConfirm}
      onOpenChange={onOpenChange}
      open={open}
      pageSize={ISSUE_POOL_PAGE_SIZE}
      serverSearch
      title="Fill out on a Project"
      variant={isMobile ? "drawer" : "dialog"}
    />
  );
}
